import moment from "moment";

import { DateTime } from "@/Common/Helpers/DateTime";
import { LuxonDateHelper } from "@/Common/Helpers/LuxonDateHelper";
import { MathHelper } from "@/Common/Helpers/MathHelper";
import { UnitSystem } from "@/Common/Models/Patient/PatientEnums";
import { AgeUnit, BaseChartModel, ChartId, VitalProperty } from "@/Patient/Models/GrowthCharts/BaseChartModel";
import { AxisConfiguration, ChartDataPoint } from "@/Patient/Models/GrowthCharts/ChartData";
import { ILineChart, ILineChartConfig } from "../../Charts/LineChartGraph";

abstract class VitalSignChartModel extends BaseChartModel implements ILineChart {
    public get secondaryVitalProperty(): VitalProperty {
        return null;
    }

    public get isPlotSelectionSupported(): boolean {
        return false;
    }

    public get displayUnitInSecondLine(): boolean {
        return false;
    }

    public filterVitalSign(): boolean {
        return true;
    }

    public isDisplayedByDefault(): boolean {
        return LuxonDateHelper.dateDiff(this.patient.DateOfBirth, DateTime.nowDate(), "years") > 19;
    }

    public isCompatible(): boolean {
        return this.vitalSigns
            .filter(vital => !!vital[this.vitalProperty] && (!this.secondaryVitalProperty || !!vital[this.secondaryVitalProperty]))
            .count() > 0;
    }

    public get isVitalSign(): boolean {
        return true;
    }

    public get ageUnit(): AgeUnit {
        return "year";
    }

    public get primary(): ILineChartConfig {
        return {
            label: this.name,
            dataPoints: this.getDataPoints(this.vitalProperty)
        };
    }

    public get secondary(): ILineChartConfig {
        return {
            label: this.secondaryVitalProperty,
            dataPoints: this.getDataPoints(this.secondaryVitalProperty)
        };
    }

    public get xAxis(): AxisConfiguration {
        return {
            label: cacheStore.GetString("DatesLabel")
        };
    }

    protected getDataPoints(field: VitalProperty): ChartDataPoint[] {
        return this.vitalSigns.toArray()
            .filter(vs => !!vs[field])
            .map(vs => ({
                id: `${this.id}_${vs.Id}`,
                medicalItemId: vs.MedicalItemId,
                x: moment(vs.PharmaStartDate).format("YYYY-MM-DD"),
                y: MathHelper.roundToDecimals(this.convertVitalSignIfNeeded(vs[field], vs.WeightUnitLookupId, vs.HeightUnitLookupId, vs.HeadCircumferenceUnitLookupId), 2).toString()
            }));
    }
}

class WeightVitalSign extends VitalSignChartModel {
    public get id(): ChartId {
        return "VSW";
    }

    public get name(): string {
        return cacheStore.GetString("WeightFullDLabel");
    }

    public get shortName(): string {
        return cacheStore.GetString("WeightAbbrevLabel");
    }

    public get yAxis(): AxisConfiguration {
        return {
            label: cacheStore.GetString("WeightLabel")
                + ` (${this.unit === UnitSystem.Metric
                    ? cacheStore.GetString("KilogramUnitSymbolLabel")
                    : cacheStore.GetString("PoundUnitSymbolLabel")})`
        };
    }

    public get vitalProperty(): VitalProperty {
        return "Weight";
    }
}

class HeightVitalSign extends VitalSignChartModel {
    public get id(): ChartId {
        return "VSH";
    }

    public get name(): string {
        return cacheStore.GetString("HeightLabel");
    }

    public get shortName(): string {
        return cacheStore.GetString("HeightAbbrevLabel");
    }

    public get yAxis(): AxisConfiguration {
        return {
            label: cacheStore.GetString("HeightLabel") + ` (${this.unit === UnitSystem.Metric ? cacheStore.GetString("CentimeterUnitSymbolLabel") : cacheStore.GetString("InchUnitSymbolLabel")})`
        };
    }

    public get vitalProperty(): VitalProperty {
        return "Height";
    }
}

class BMIVitalSign extends VitalSignChartModel {
    private static DEFAULT_Y_AXIS_MIN: number = 11;
    private static DEFAULT_Y_AXIS_MAX: number = 32;

    public get id(): ChartId {
        return "VSBMI";
    }

    public get name(): string {
        return cacheStore.GetString("MassBodyIndexDLabel");
    }

    public get shortName(): string {
        return cacheStore.GetString("BMILabel");
    }

    public get yAxis(): AxisConfiguration {
        return {
            label: cacheStore.GetString("MassBodyIndexDLabel"),
            min: this.CalculateYAxisMinValue(),
            max: this.CalculateYAxisMaxValue(),
            interval: 1
        };
    }

    public get vitalProperty(): VitalProperty {
        return "MassBodyIndex";
    }

    private CalculateYAxisMaxValue(): number {
        const maxValue: number = this.GetYAxisValues().max();
        if (isNaN(maxValue))
            return BMIVitalSign.DEFAULT_Y_AXIS_MAX;

        return maxValue > BMIVitalSign.DEFAULT_Y_AXIS_MAX ? maxValue * 1.1 : BMIVitalSign.DEFAULT_Y_AXIS_MAX;
    }

    private CalculateYAxisMinValue(): number {
        const minValue: number = this.GetYAxisValues().min();
        if (isNaN(minValue))
            return BMIVitalSign.DEFAULT_Y_AXIS_MIN;

        return minValue < BMIVitalSign.DEFAULT_Y_AXIS_MIN ? minValue * 0.9 : BMIVitalSign.DEFAULT_Y_AXIS_MIN;
    }

    private GetYAxisValues(): number[] {
        return this.primary.dataPoints.map(point => Number(point.y));
    }
}

class BloodPressureVitalSign extends VitalSignChartModel {
    public get id(): ChartId {
        return "VSPB";
    }

    public get name(): string {
        return cacheStore.GetString("BloodPressureLabel");
    }

    public get shortName(): string {
        return cacheStore.GetString("BloodPressureAbbrevLabel");
    }

    public get displayUnitInSecondLine(): boolean {
        return true;
    }

    public get yAxis(): AxisConfiguration {
        return {
            label: `${cacheStore.GetString("BloodPressureLabel")}\n(${cacheStore.GetString("MillimetreOfMercuryAbbrvLabel")})`
        };
    }

    public get vitalProperty(): VitalProperty {
        return "BPSys";
    }

    public get secondaryVitalProperty(): VitalProperty {
        return "BPDia";
    }

    public get primary(): ILineChartConfig {
        return {
            label: `${cacheStore.GetString("SystolicLabel")}\n(${cacheStore.GetString("MillimetreOfMercuryAbbrvLabel")})`,
            dataPoints: this.getDataPoints(this.vitalProperty)
        };
    }

    public get secondary(): ILineChartConfig {
        return {
            label: `${cacheStore.GetString("DiastolicLabel")}\n(${cacheStore.GetString("MillimetreOfMercuryAbbrvLabel")})`,
            dataPoints: this.getDataPoints(this.secondaryVitalProperty)
        };
    }
}

class HeartRateVitalSign extends VitalSignChartModel {
    public get id(): ChartId {
        return "VSHR";
    }

    public get name(): string {
        return cacheStore.GetString("HRLabel");
    }

    public get shortName(): string {
        return cacheStore.GetString("HeartRateAbbreviationLabel");
    }

    public get vitalProperty(): VitalProperty {
        return "HeartRate";
    }

    public get yAxis(): AxisConfiguration {
        return {
            label: `${cacheStore.GetString("HRLabel")} (${cacheStore.GetString("BeatsPerMinuteAbbrevLabel")})`
        };
    }
}

export {
    BloodPressureVitalSign,
    BMIVitalSign,
    HeartRateVitalSign,
    HeightVitalSign,
    VitalSignChartModel,
    WeightVitalSign
};
